import { Button, cn, Input, Label, Skeleton } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Plus, X } from 'lucide-react';
import { type ClipboardEvent, type FormEvent, useId, useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { EXCHANGE_MAINTENANCE_QUERY_KEY, useExchangeMaintenance } from '@/hooks/queries/exchange/use-exchange-maintenance';
import {
  ALLOWLIST_SEGMENTS,
  type AllowlistSegment,
  type ExchangeMaintenanceAllowlist,
  type ExchangeMaintenanceResponse,
  setExchangeMaintenanceAllowlist,
} from '@/services/api/exchange-maintenance';
import {
  describeAllowlist,
  describeChanges,
  isEmptyAllowlist,
  isSameAllowlist,
  MAX_ALLOWLIST_ACCOUNT_HOLDER_IDS,
  parseAccountHolderIds,
  SEGMENT_COPY,
} from './maintenance-allowlist';

interface SegmentTileProps {
  segment: AllowlistSegment;
  isSelected: boolean;
  disabled: boolean;
  onToggle: () => void;
}

/** A segment as a toggle: the whole tile is the control, so it reads as a choice rather than a form row. */
const SegmentTile = ({ segment, isSelected, disabled, onToggle }: SegmentTileProps) => {
  const copy = SEGMENT_COPY[segment];
  return (
    <button
      aria-pressed={isSelected}
      className={cn(
        'flex min-h-20 flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
        isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-foreground/30 hover:bg-muted/50',
      )}
      disabled={disabled}
      onClick={onToggle}
      type="button"
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="font-medium">{copy.label}</span>
        <span
          aria-hidden
          className={cn('flex size-5 shrink-0 items-center justify-center rounded border', isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-border')}
        >
          {isSelected ? <Check className="size-3.5" /> : null}
        </span>
      </span>
      <span className="text-xs text-muted-foreground">{copy.description}</span>
      {copy.isEnforced ? null : <span className="mt-auto text-xs text-amber-500">Not enforced yet</span>}
    </button>
  );
};
SegmentTile.displayName = 'SegmentTile';

interface AllowlistEditorProps {
  saved: ExchangeMaintenanceAllowlist;
  canEdit: boolean;
  isOn: boolean;
}

/** Keyed on the saved list, so a save made elsewhere resets the draft rather than being overwritten by it. */
const AllowlistEditor = ({ saved, canEdit, isOn }: AllowlistEditorProps) => {
  const queryClient = useQueryClient();
  const fieldId = useId();
  const [segments, setSegments] = useState<AllowlistSegment[]>(saved.segments);
  const [ids, setIds] = useState<string[]>(saved.account_holder_ids);
  const [entry, setEntry] = useState('');
  const [invalid, setInvalid] = useState<string[]>([]);

  const draft: ExchangeMaintenanceAllowlist = { segments, account_holder_ids: ids };
  const isDirty = !isSameAllowlist(draft, saved);
  const isFull = ids.length >= MAX_ALLOWLIST_ACCOUNT_HOLDER_IDS;

  const toggleSegment = (segment: AllowlistSegment) => setSegments((current) => (current.includes(segment) ? current.filter((s) => s !== segment) : [...current, segment]));

  /** Adds every id in `text` (one or many, pasted or typed); anything that is not a UUID stays in the box to fix. */
  const addIds = (text: string) => {
    const parsed = parseAccountHolderIds(text);
    const room = MAX_ALLOWLIST_ACCOUNT_HOLDER_IDS - ids.length;
    const fresh = parsed.ids.filter((id) => !ids.includes(id));
    setIds((current) => [...current, ...fresh.slice(0, room)]);
    setInvalid(parsed.invalid);
    setEntry(parsed.invalid.join(' '));
    if (fresh.length > room) toast.warning(`Only ${MAX_ALLOWLIST_ACCOUNT_HOLDER_IDS} staff accounts fit; ${fresh.length - room} were not added`);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (entry.trim()) addIds(entry);
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    // A paste of several ids is added at once; a single one waits for Enter like typed input
    if (/[\s,]/.test(text.trim())) {
      event.preventDefault();
      addIds(text);
    }
  };

  const discard = () => {
    setSegments(saved.segments);
    setIds(saved.account_holder_ids);
    setEntry('');
    setInvalid([]);
  };

  const save = () => {
    void confirmAction({
      title: isEmptyAllowlist(draft) ? 'Let nobody in during maintenance' : 'Change who is let in during maintenance',
      description: isEmptyAllowlist(draft)
        ? 'Nobody will be let in during maintenance; every request that reaches the exchange is refused. This action is logged and auditable.'
        : `${describeAllowlist(draft)} will reach the exchange ${isOn ? 'straight away; everyone else stays refused' : 'from the moment maintenance starts'}. This action is logged and auditable.`,
      actionLabel: 'Save changes',
      destructive: isOn && !isEmptyAllowlist(draft),
      // A throw keeps the dialog open showing the API error (403, 400, or saved-but-not-propagated)
      onConfirm: async (comment) => {
        let result: Awaited<ReturnType<typeof setExchangeMaintenanceAllowlist>>;
        try {
          result = await setExchangeMaintenanceAllowlist(draft, comment);
        } catch (error) {
          // The write may still have landed (saved but not propagated): refresh in the background
          void queryClient.invalidateQueries({ queryKey: EXCHANGE_MAINTENANCE_QUERY_KEY });
          throw error;
        }
        queryClient.setQueryData<ExchangeMaintenanceResponse>(EXCHANGE_MAINTENANCE_QUERY_KEY, (current) => (current ? { ...current, ...result } : current));
        toast.success('Changes saved');
      },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-3 text-sm font-medium">Segments</legend>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {ALLOWLIST_SEGMENTS.map((segment) => (
            <SegmentTile disabled={!canEdit} isSelected={segments.includes(segment)} key={segment} onToggle={() => toggleSegment(segment)} segment={segment} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Market makers, institutional and retail take effect once fast-api and bun-public check maintenance.</p>
      </fieldset>

      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <Label htmlFor={`${fieldId}-ids`}>Staff accounts</Label>
          <span className="text-xs text-muted-foreground">
            {ids.length} of {MAX_ALLOWLIST_ACCOUNT_HOLDER_IDS}
          </span>
        </div>
        <form className="flex gap-2" onSubmit={onSubmit}>
          <Input
            aria-describedby={`${fieldId}-ids-help`}
            aria-invalid={invalid.length > 0}
            className="font-mono text-xs"
            disabled={!canEdit || isFull}
            id={`${fieldId}-ids`}
            onChange={(event) => {
              setEntry(event.target.value);
              setInvalid([]);
            }}
            onPaste={onPaste}
            placeholder="Paste account holder ids"
            value={entry}
          />
          <Button className="gap-1.5" disabled={!canEdit || isFull || !entry.trim()} type="submit" variant="outline">
            <Plus className="size-4" />
            Add
          </Button>
        </form>
        <p className={cn('text-xs', invalid.length ? 'text-destructive' : 'text-muted-foreground')} id={`${fieldId}-ids-help`}>
          {invalid.length
            ? `Not an account holder id: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? ` and ${invalid.length - 3} more` : ''}`
            : 'Paste several at once, separated by spaces, commas or new lines. Matched on fast-api and bun-public.'}
        </p>
        {ids.length ? (
          <ul aria-label="Staff accounts let in" className="flex flex-wrap gap-2">
            {ids.map((id) => (
              <li className="inline-flex items-center gap-1 rounded-md border bg-muted/40 py-1 pl-2.5 pr-1 font-mono text-xs" key={id}>
                {id}
                <button
                  aria-label={`Remove ${id}`}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
                  disabled={!canEdit}
                  onClick={() => setIds((current) => current.filter((existing) => existing !== id))}
                  type="button"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {isDirty ? (
        <div className="flex flex-col gap-3 rounded-md border bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" role="status">
          <span className="text-sm">{describeChanges(saved, draft)}</span>
          <div className="flex gap-2">
            <Button onClick={discard} type="button" variant="ghost">
              Discard
            </Button>
            <Button disabled={!canEdit} onClick={save} type="button">
              Save changes
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Who reaches the exchange while maintenance is on, so traffic can be let back in gradually
 * (staff, then a segment at a time) before maintenance is ended. Cleared when maintenance ends.
 */
export const MaintenanceAllowlistSection = () => {
  const { data } = useExchangeMaintenance();
  const isOn = data?.state === 'on';

  return (
    <section aria-labelledby="let-traffic-back-in" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold" id="let-traffic-back-in">
          Let traffic back in
        </h2>
        <p className="max-w-prose text-sm text-muted-foreground">
          {isOn
            ? 'Choose who reaches the exchange before you end maintenance. Everyone else stays refused.'
            : 'Choose who reaches the exchange while maintenance is on. You can set this up before a window; ending maintenance clears it.'}
        </p>
      </div>
      {data ? <AllowlistEditor canEdit={data.canEdit} isOn={isOn} key={JSON.stringify(data.allowlist)} saved={data.allowlist} /> : <Skeleton className="h-48 w-full" />}
    </section>
  );
};
MaintenanceAllowlistSection.displayName = 'MaintenanceAllowlistSection';
